import { FlatTreeControl } from '@angular/cdk/tree';
import { Injectable } from '@angular/core';
import { ProjectFileSystemType } from '@portal-core/project-files/enums/project-file-system-type.enum';
import { ProjectFileUpdateAction } from '@portal-core/project-files/enums/project-file-update-action.enum';
import { ProjectFileManualUpdateAction } from '@portal-core/project-files/models/project-file-manual-update-action.model';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectUncommittedFile } from '@portal-core/project-files/models/project-uncommitted-file.model';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { ProjectFileFlatNode } from '@portal-core/project-files/util/project-file-flat-node';
import { ProjectFileNode } from '@portal-core/project-files/util/project-file-node';
import { BehaviorSubject, catchError, map, Observable, of, Subject, switchMap } from 'rxjs';
import { ProjectFilesChecklistService } from './project-files-checklist.service';

@Injectable()
/**
 *  Only meant to be used by ProjectFilesTreeComponent as a provider.
 *  This service is used to create/manipulate normal Nodes for the tree used in ProjectFilesTreeComponent.
**/
export class ProjectFilesTreeDataService {

  projectId: number;
  includeFiles: boolean;
  uncommittedFiles: ProjectUncommittedFile[] = [];
  fileFilter: string;
  committedFilter: boolean = null;
  dataChange: BehaviorSubject<ProjectFileNode[]> = new BehaviorSubject<[]>([]);
  manuallyEditedTreeData: BehaviorSubject<ProjectFileManualUpdateAction> = new BehaviorSubject<ProjectFileManualUpdateAction>(null);
  nodeMap: Map<string, ProjectFileNode> = new Map<string, ProjectFileNode>();
  retry$: Subject<void> = new Subject();
  treeControl: FlatTreeControl<ProjectFileFlatNode>;
  toggleTreeNode: BehaviorSubject<ProjectFileFlatNode> = new BehaviorSubject(null);

  constructor(private projectFilesService: ProjectFilesService, private projectFilesChecklistService: ProjectFilesChecklistService) {
  }

  initialize(projectFiles: ProjectFile[]): ProjectFileNode[] {
    return this.generateNodes(projectFiles);
  }

  getNode(key: string): ProjectFileNode {
    return this.nodeMap.get(key);
  }

  removeNode(key: string): ProjectFileNode {
    const removedNode = this.nodeMap.get(key);
    this.nodeMap.delete(key);
    this.manuallyEditedTreeData.next({ action: ProjectFileUpdateAction.Remove, filePath: key });
    return removedNode;
  }

  addNode(key: string, node: ProjectFileNode) {
    this.nodeMap.set(key, node);
    this.manuallyEditedTreeData.next({ action: ProjectFileUpdateAction.Add, filePath: key, addNode: node });
  }

  setTree(processedNodes: ProjectFileNode[], pathFilters: string[], ignoreInitialFilters: boolean) {
    this.dataChange.next(processedNodes);
    processedNodes.forEach(node => this.projectFilesChecklistService.setSelection(node, pathFilters, ignoreInitialFilters));
  }

  // if the new uncommitedFiles doesn't have a file from the old then its something that needs to be removed.
  getNodePathsToUpdate(uncommittedFiles: ProjectUncommittedFile[], toDelete: boolean = false, ignoreFilePaths: string[] = [], fileFilter: string = '.*'): string[] {
    let nodesToUpdate: ProjectUncommittedFile[] = [];
    const fileFilterRegex = new RegExp(fileFilter ?? '.*');
    if (toDelete) {
      // If a uncommittedFile doesn't exist in the new one then delete
      nodesToUpdate = this.uncommittedFiles.filter(oldUncommittedFile => !uncommittedFiles.find(newUncommittedFile => oldUncommittedFile.filePath === newUncommittedFile.filePath))
    } else {
      // if a uncommittedFile doesn't exist in the old one then add.
      nodesToUpdate = uncommittedFiles.filter(newUncommittedFile => !this.uncommittedFiles.find(oldUncommittedFile => newUncommittedFile.filePath === oldUncommittedFile.filePath && fileFilterRegex.test(newUncommittedFile.filePath)));
    }

    return nodesToUpdate.map(uncommittedFile => uncommittedFile.filePath).filter(path => !ignoreFilePaths.includes(path));
  }


  setTreeSelection(pathFilters: string[]) {
    this.nodeMap.forEach(node => this.projectFilesChecklistService.setSelection(node, pathFilters ? pathFilters : []));
  }

  private generateNodes(projectFiles: ProjectFile[]): ProjectFileNode[] {
    const nodes: ProjectFileNode[] = [];
    projectFiles?.forEach(file => {
      const existingNode = this.nodeMap.get(file.Path);
      if (existingNode) {
        nodes.push(existingNode);
      } else {
        const node = new ProjectFileNode(file.Name, file.Id, file.Path, null, null, file.Type === 0, file.IsCollabFile);
        this.nodeMap.set(file.Path, node);
        nodes.push(node);
      }
    });

    return nodes;
  }

  getChildren$(path: string, branchName: string): Observable<ProjectFileNode[]> {
    return this.projectFilesService.getProjectFileTree$(this.projectId, branchName, path, this.includeFiles, this.fileFilter, false, this.uncommittedFiles, this.committedFilter).pipe(
      //if its 404 error its very possible its collab files.
      catchError((err) => {
        if (err.status === 404) {
          return of(this.projectFilesService.getMissingFiles([], this.uncommittedFiles, path, this.includeFiles)
            .sort((a, b) => {
              // folders first
              if (a.Type !== b.Type) {
                return a.Type === ProjectFileSystemType.Folder ? -1 : 1
              }
              return a.Path.localeCompare(b.Path)
            })).pipe(
              switchMap((missingFiles) => {
                if (missingFiles?.length) {
                  return of(missingFiles);
                } else {
                  const flatNode = this.getNode(path)?.flatFileNode;
                  if (flatNode) {
                    flatNode.loaded = true;
                    flatNode.isLoading = false;
                    flatNode.error = true;
                    this.toggleTreeNode.next(flatNode);
                  }
                  return of(null);
                }
              }
              ));
        } else {
          const flatNode = this.getNode(path)?.flatFileNode;
          if (flatNode) {
            flatNode.loaded = false;
            flatNode.isLoading = false;
            flatNode.error = true;
            this.toggleTreeNode.next(flatNode);
          }
          return of(null);
        }
      }),
      map(projectFiles => this.generateNodes(projectFiles))
    );
  }

  addChildrenToFolderNode(node: ProjectFileNode, nodes: ProjectFileNode[], pathFilters: string[], ignoreInitialFilters: boolean) {
    node.flatFileNode.error = false;
    node.flatFileNode.isLoading = false;
    node.flatFileNode.loaded = true;
    node.childrenChange.next(nodes);
    this.dataChange.next(this.dataChange.value);
    if (!ignoreInitialFilters) {
      nodes.forEach(childNode => { childNode.parentNode = node; this.projectFilesChecklistService.setSelection(childNode, pathFilters); });
    }
  }

  addNewFileNodeToTree(node: ProjectFileFlatNode, newProjectFileNode: ProjectFileNode) {
    const parent = this.getNode(node.path);
    parent.children.push(newProjectFileNode);
    parent.childrenChange.next(parent.children);
    this.dataChange.next(this.dataChange.value);
  }


}
